/**
 * Pure viewport culling for dense marker layers. Bounds the number of RENDERED
 * markers regardless of dataset size via three composable gates — zoom-gate →
 * visible-box filter → nearest-to-centre cap — so layers like CCTV (~3,263) can
 * render without stalling 3D. Opt-in per layer (manifest `viewportCulled`); all
 * other layers pass through untouched. No map/DOM API, no regex.
 */

import { CLIENT_CONFIG } from '@/config';
import { findLayerDefinition } from '@/components/map/plugin-layer-manager';
import { useMapViewportStore, type ViewportBox } from '@/stores/map-viewport-store';
import type { MarkerData } from './marker-state';

/**
 * Cull a layer's markers to the current viewport. Pass-through when the layer is
 * not viewport-culled or the viewport is not yet known (never blanks a layer
 * before the first camera settle).
 */
export function cullMarkersForViewport(markers: MarkerData[], layerId: string): MarkerData[] {
  const definition = findLayerDefinition(layerId);
  if (!definition?.viewportCulled) return markers;

  const { box, center, active } = useMapViewportStore.getState();
  if (!box || !center) return markers;
  if (!active) return [];

  const inView = markers.filter((marker) => inBox(marker.position, box));
  const cap = definition.maxRendered ?? CLIENT_CONFIG.viewportDefaultMaxRendered;
  return inView.length <= cap ? inView : nearest(inView, center, cap);
}

/** Whether a point lies inside the box (antimeridian-aware on longitude). */
function inBox(point: google.maps.LatLngLiteral, box: ViewportBox): boolean {
  if (point.lat < box.south || point.lat > box.north) return false;
  return box.west <= box.east
    ? point.lng >= box.west && point.lng <= box.east
    : point.lng >= box.west || point.lng <= box.east;
}

/** Keep the `cap` markers nearest the viewport centre (cheap squared planar distance). */
function nearest(markers: MarkerData[], center: { lat: number; lng: number }, cap: number): MarkerData[] {
  return [...markers].sort((a, b) => squaredDistance(a, center) - squaredDistance(b, center)).slice(0, cap);
}

/** Squared planar distance from a marker to the centre (monotonic — ranking only). */
function squaredDistance(marker: MarkerData, center: { lat: number; lng: number }): number {
  const dLat = marker.position.lat - center.lat;
  const dLng = marker.position.lng - center.lng;
  return dLat * dLat + dLng * dLng;
}
